import { CallStatus } from "@prisma/client";
import { useQuery } from "@tanstack/react-query";
import { getUserCalls } from "@/app/actions/call/get/action";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { GetUserCallsResult } from "@/app/types/call/GetUserCallsResult";
import { ProcessingJobData } from "@/app/types/job/ProcessingJob";
import { UCallScope } from "@/app/utils/UCallScope";
import { UJobPolling } from "@/app/utils/UJobPolling";
import { Globals } from "@/app/vars/Globals";
import { QueryKey } from "@/app/vars/QueryKeys";

export function buildCallsQueryKey(
  projectId: number | undefined,
  page: number,
  limit: number,
  status: CallStatus | null | undefined,
  filter: CallScopeFilter,
) {
  return [
    QueryKey.CALLS,
    projectId,
    page,
    limit,
    status === undefined ? "all" : status,
    ...UCallScope.queryKey(filter),
  ];
}

export function useFetchCalls(
  options: {
    page?: number;
    limit?: number;
    projectId?: number;
    status?: CallStatus | null;
    filter?: CallScopeFilter;
    enabled?: boolean;
    isCallAnalysisPage?: boolean;
    runningJobs?: ProcessingJobData[];
  } = {},
) {
  const {
    page = 1,
    limit = Globals.CALLS_PER_PAGE,
    projectId,
    status,
    filter = {},
    enabled = true,
    isCallAnalysisPage,
    runningJobs,
  } = options;

  return useQuery({
    queryKey: buildCallsQueryKey(projectId, page, limit, status, filter),
    queryFn: async () => {
      return await getUserCalls(page, limit, projectId, status, filter);
    },
    enabled: enabled && !!projectId,
    staleTime: Infinity,
    refetchInterval: (query) => {
      const interval = UJobPolling.getCallAnalysisInterval(runningJobs, {
        isCallAnalysisPage,
      });
      if (interval) return interval;

      const data = query.state.data as GetUserCallsResult | undefined;
      if (isCallAnalysisPage && data?.calls.some((c) => c.status === CallStatus.ANALYZING)) {
        return Globals.ACTIVE_JOB_POLLING_INTERVAL_MS;
      }
      return false;
    },
  });
}
